import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Loader2, CheckCircle2, AlertTriangle, Building2, User, Mail, 
  Phone, MapPin, Package, ArrowRight, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

const BUSINESS_TYPES = [
  { value: "manufacturer", label: "Manufacturer" },
  { value: "distributor", label: "Distributor" },
  { value: "repackager", label: "Repackager/Relabeler" },
  { value: "wholesaler", label: "Wholesaler" },
  { value: "medical_equipment", label: "Medical Equipment Supplier" },
  { value: "surgical_supplies", label: "Surgical Supplies" },
  { value: "sterile_processing", label: "Sterile Processing Equipment" },
  { value: "other", label: "Other" }
];

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function SupplierRequestAccessPage() {
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";
  const source = searchParams.get("source") || "direct";
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: prefilledEmail,
    phone: "",
    city: "",
    state: "",
    business_type: "",
    fei_number: "",
    product_categories: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name || !formData.email || !formData.phone || !formData.state || !formData.business_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/suppliers/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          source,
          requested_at: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to submit request");
      }
      
      setSubmitted(true);
      toast.success("Your request has been submitted!");
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-violet-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Request Received!</h1>
          <p className="text-slate-400 mb-6">
            Thank you for your interest in joining the AskIsaac supplier network. Our team will review your 
            information and send you an invitation link within 24-48 hours.
          </p>
          <p className="text-slate-500 text-sm mb-8">
            We verify all suppliers to ensure quality products for healthcare facilities.
          </p>
          <Link to="/">
            <Button className="bg-violet-600 hover:bg-violet-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-10" />
          </Link>
          <Link to="/join-network?type=supplier" className="text-slate-400 hover:text-white text-sm">
            ← Back to Overview
          </Link>
        </div>
      </div>
      
      {/* Form Section */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/40 text-violet-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Package className="w-4 h-4" />
            JOIN THE HEALTHCARE SUPPLIER NETWORK
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Request to Join as a Supplier</h1>
          <p className="text-slate-400">
            Fill out the form below and our team will verify your business and send you an invitation.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
          <div className="space-y-6">
            {/* Company Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="MedSupply Inc"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Contact Name</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Jane Doe"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jane@medsupply.com"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            {/* Location */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Dallas"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">State *</Label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white mt-1"
                  data-testid="state-select"
                >
                  <option value="">Select State</option>
                  {STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Business Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Business Type *</Label>
                <select
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white mt-1"
                  data-testid="business-type-select"
                >
                  <option value="">Select Type</option>
                  {BUSINESS_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-slate-300">FEI Number (if FDA registered)</Label>
                <Input
                  value={formData.fei_number}
                  onChange={(e) => setFormData({ ...formData, fei_number: e.target.value })}
                  placeholder="3012345678"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            
            {/* Products */}
            <div>
              <Label className="text-slate-300">Product Categories</Label>
              <Input
                value={formData.product_categories}
                onChange={(e) => setFormData({ ...formData, product_categories: e.target.value })}
                placeholder="Surgical instruments, sterilization equipment, PPE..."
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            
            {/* Message */}
            <div>
              <Label className="text-slate-300">Anything else we should know?</Label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your products, certifications, or coverage area..."
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-slate-700 border border-slate-600 text-white mt-1 resize-none"
              />
            </div>
            
            {/* What happens next */}
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <h4 className="text-white font-medium mb-2">What happens next?</h4>
              <ol className="text-slate-400 text-sm space-y-1">
                <li>1. Our team reviews your company information</li>
                <li>2. We verify your FDA registration (if applicable)</li>
                <li>3. You receive an email invitation within 24-48 hours</li>
                <li>4. Set up your supplier profile and start receiving RFQs</li>
              </ol>
            </div>
            
            {/* Submit */}
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 h-auto text-lg"
              data-testid="submit-request"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Request to Join
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-slate-500 text-xs text-center">
              By submitting this form, you agree to be contacted about joining the AskIsaac supplier network.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
